import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  Timestamp,
  arrayUnion
} from "firebase/firestore";
import { db } from "../firebase"; // Your Firebase config file
import "./AddonRequest.css";

const AddonRequest = () => {
  const [addonRequests, setAddonRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAddonRequests = async () => {
      try {
        setLoading(true);
        // This query specifically fetches ONLY the documents that are requests (status: "pending")
        const q = query(collection(db, "addon_requests"), where("status", "==", "pending"));
        const data = await getDocs(q);
        setAddonRequests(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
        setError(null);
      } catch (err) {
        setError("Failed to fetch data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAddonRequests();
  }, []);

  // This function is the core of the "approval" action
  const handleApproveRequest = async (requestId) => {
    setError(null);

    try {
      // A transaction ensures the entire approval process succeeds or fails together
      await runTransaction(db, async (transaction) => {
        const requestDocRef = doc(db, 'addon_requests', requestId);
        const requestDoc = await transaction.get(requestDocRef);
        if (!requestDoc.exists()) throw new Error("Request not found!");
        
        const requestData = requestDoc.data();
        const customerId = requestData.customerId;
        
        // 1. It reads the total cost of the entire order
        const orderCost = Number(requestData.totalOrderCost);

        // 2. It finds the customer's wallet to deduct coins from
        const walletsRef = collection(db, 'customer_wallets');
        const walletQuery = query(walletsRef, where("customerId", "==", customerId));
        const walletSnapshot = await getDocs(walletQuery);
        if (walletSnapshot.empty) throw new Error(`Wallet not found for this customer.`);
        
        const walletDoc = walletSnapshot.docs[0];
        const walletDocRef = walletDoc.ref;
        const walletData = walletDoc.data();
        
        const currentBalance = Number(walletData.walletCoins);

        // 3. It checks for sufficient funds and deducts the cost
        if (currentBalance < orderCost) {
          throw new Error(`Insufficient funds. Wallet has ${currentBalance}, but the order costs ${orderCost}.`);
        }
        const newBalance = currentBalance - orderCost;
        const newDebitTransaction = {
          amount: orderCost,
          description: `Addon order with ${requestData.items.length} items approved.`,
          source: "addon_request_order",
          timestamp: Timestamp.now(),
          type: "debit"
        };
        transaction.update(walletDocRef, {
            walletCoins: newBalance,
            transactions: arrayUnion(newDebitTransaction)
        });
        
        // 4. It moves the approved request to the 'Addon_Approved' collection
        const newDocRef = doc(collection(db, 'Addon_Approved'));
        const approvedData = { ...requestData, status: 'approved', walletDeducted: true, approvedAt: Timestamp.now() };
        transaction.set(newDocRef, approvedData);
        transaction.delete(requestDocRef); // Deletes the original pending request
      });

      // 5. It removes the request from the screen
      console.log("Order approval successful!");
      setAddonRequests(prev => prev.filter(req => req.id !== requestId));

    } catch (e) {
      console.error("Transaction failed: ", e.message);
      setError(`Approval failed: ${e.message}`);
    }
  };

  return (
    <div className="addon-request-container">
      <h1>Addon Requests</h1>
      {error && <div className="error-message">{error}</div>}
      <div className="table-wrapper">
        <table className="request-table">
            <thead>
                <tr>
                    <th>Customer Name</th>
                    <th>Requested At</th>
                    <th>Items in Order</th>
                    <th>Total Order Cost</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                {addonRequests.length > 0 ? (
                    addonRequests.map((request) => (
                        <tr key={request.id}>
                            <td>{request.customerName}</td>
                            <td>{new Date(request.requestedAt.seconds * 1000).toLocaleString()}</td>
                            <td>
                                <ul className="item-list">
                                    {request.items && request.items.map((item, index) => (
                                        <li key={index}>
                                            {item.foodItemName} (Qty: {item.quantity})
                                        </li>
                                    ))}
                                </ul>
                            </td>
                            <td>{request.totalOrderCost}</td>
                            <td>
                                {/* This button triggers the approval function */}
                                <button className="approve-button" onClick={() => handleApproveRequest(request.id)}>
                                    Approve Order
                                </button>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr><td colSpan="5">No pending addon requests found.</td></tr>
                )}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default AddonRequest;